import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";
import { API, useApi, errMsg, fmtDate, slug } from "@/lib/api";
import { ImportPanel } from "@/components/admin/ImportPanel";
import { LiveDataPanel } from "@/components/admin/LiveDataPanel";
import { AlertsLogPanel } from "@/components/admin/AlertsLogPanel";
import { BackupsPanel } from "@/components/admin/BackupsPanel";
import { AIModelPanel } from "@/components/admin/AIModelPanel";
import { RecapPanel } from "@/components/admin/RecapPanel";
import { PaywallPanel } from "@/components/admin/PaywallPanel";
import { KrokPanel } from "@/components/admin/KrokPanel";
import { BetfairPanel } from "@/components/admin/BetfairPanel";
import { RQPanel } from "@/components/admin/RQPanel";
import { MarketCheckPanel } from "@/components/admin/MarketCheckPanel";
import { LessonsPanel } from "@/components/analyst/LessonsPanel";
import { SheetsPanel } from "@/components/admin/SheetsPanel";
import { TrackPicker } from "@/components/admin/TrackPicker";
import { ReviewTable } from "@/components/admin/ReviewTable";
import { useMember } from "@/components/MemberProvider";

const UsersPanel = () => {
  const api = useApi();
  const [data, setData] = useState({ roles: [], users: [] });
  const load = useCallback(() => api("get", "/users").then(setData).catch((e) => toast.error(errMsg(e))), [api]);
  useEffect(() => { load(); }, [load]);
  const change = async (uid, role) => {
    try { await api("patch", `/users/${uid}/role`, { role }); toast.success("Role updated"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <section className="rounded-2xl bg-[#131C31] border border-white/5 p-5" data-testid="admin-users-panel">
      <h2 className="font-display text-lg font-bold mb-3">Members</h2>
      {data.users.map((u) => (
        <div key={u.user_id} className="flex items-center gap-2 py-2 border-t border-white/5 text-sm">
          <span className="truncate mr-auto">{u.email || u.user_id}</span>
          <select data-testid={`role-select-${u.user_id}`} value={u.role} onChange={(e) => change(u.user_id, e.target.value)}
            className="bg-[#0B101D] border border-white/10 rounded px-2 py-1 text-xs">
            {data.roles.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      ))}
    </section>
  );
};

export default function Admin() {
  const { me, isAdmin } = useMember();
  const api = useApi();
  const [draft, setDraft] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const load = () => axios.get(`${API}/meetings`).then((r) => setMeetings(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (m) => {
    if (!window.confirm(`Delete ${m.track} ${m.date}?`)) return;
    try { await api("delete", `/admin/meetings/${m.id}`); toast.success("Meeting deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  if (!me) return <p className="p-10 text-center text-slate-400">Loading…</p>;
  if (!isAdmin) return <p className="p-10 text-center text-slate-400" data-testid="admin-forbidden">Admins only.</p>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 space-y-6" data-testid="admin-page">
      <h1 className="text-4xl sm:text-5xl font-black uppercase tracking-tight">Form Desk</h1>
      <LiveDataPanel onImported={load} />
      {draft ? <ReviewTable meeting={draft} setMeeting={setDraft} onSaved={() => { setDraft(null); load(); }} /> : <ImportPanel onExtracted={setDraft} />}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <section className="lg:col-span-8 rounded-2xl bg-[#131C31] border border-white/5 p-5" data-testid="admin-meetings-panel">
          <h2 className="font-display text-lg font-bold mb-3">Meetings</h2>
          {meetings.map((m) => (
            <div key={m.id} className="flex items-center gap-3 py-2 border-t border-white/5 text-sm">
              <Link to={`/meeting/${m.id}`} className="mr-auto hover:text-blue-400">{m.track} <span className="text-slate-500">· {fmtDate(m.date)} · {m.races?.length} races</span>
                {m.is_sample && <span className="ml-2 text-[10px] uppercase text-slate-500">sample</span>}</Link>
              <button data-testid={`admin-delete-${slug(m.track)}-${m.date}`} onClick={() => remove(m)} className="p-1.5 text-slate-400 hover:text-rose-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </section>
        <div className="lg:col-span-4 space-y-6"><TrackPicker /><LessonsPanel /><MarketCheckPanel /><RQPanel /><BetfairPanel /><KrokPanel /><PaywallPanel /><AIModelPanel /><AlertsLogPanel /><RecapPanel /><BackupsPanel /><UsersPanel /></div>
      </div>
      <SheetsPanel />
    </div>
  );
}
